import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { PublicClientApplication, type AccountInfo } from "@azure/msal-browser";
import type { TriyoUser, LoginResponse } from "@/types/models";
import { AuthApi } from "@/services/api/auth-api";
import { setAuthToken, removeAuthToken, getToken, decodeJwt, getSsoCookieToken } from "./auth-utils";
import { isLoginMethodEnabled } from "./auth-config";
import { openTriyoWebLogin, waitForToken } from "./sso-web";
import { msalConfig } from "./msal-config";
import { AuthContext } from "./auth-context-store";

const msalInstance = new PublicClientApplication(msalConfig);

function readClaim(claims: Record<string, unknown>, key: string): string {
  const value = claims[key];
  return typeof value === "string" ? value : "";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<TriyoUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const ssoAbortRef = useRef<AbortController | null>(null);

  const applyLogin = useCallback((data: LoginResponse, remember = true) => {
    if (!data || !data.Token) throw new Error("Token was not received from the TRIYO API.");
    if (data.User && data.User.Active === false) {
      throw new Error("This user account is no longer active. Contact your administrator.");
    }
    setAuthToken(data.Token, data.AccessToken ?? "", data.GraphToken ?? "", remember);
    setUser(data.User ?? null);
    setIsAuthenticated(true);
  }, []);

  const applyCurrentUser = useCallback(
    (data: { User: TriyoUser; Token?: string; AccessToken?: string; GraphToken?: string }, fallbackToken?: string) => {
      const token = data.Token || fallbackToken;
      if (!token) throw new Error("Token was not received from the TRIYO API.");
      if (data.User && data.User.Active === false) {
        throw new Error("This user account is no longer active. Contact your administrator.");
      }
      setAuthToken(token, data.AccessToken ?? "", data.GraphToken ?? "");
      setUser(data.User ?? null);
      setIsAuthenticated(true);
    },
    []
  );

  // On launch: resume from a stored token, else try an existing web SSO cookie
  // (only when the legacy web SSO method is enabled in config).
  useEffect(() => {
    let cancelled = false;
    const resume = async () => {
      try {
        const stored = getToken();
        if (stored) {
          const data = await AuthApi.getCurrentUser();
          if (cancelled) return;
          if (data.User && data.User.Active !== false) {
            setUser(data.User);
            setIsAuthenticated(true);
            return;
          }
        }

        if (isLoginMethodEnabled("legacySso")) {
          const ssoToken = getSsoCookieToken();
          if (ssoToken) {
            const data = await AuthApi.getCurrentUserWithToken(ssoToken);
            if (cancelled) return;
            applyCurrentUser(data, ssoToken);
          }
        }
      } catch {
        // Stored/cookie token invalid — fall through to the login screen.
      } finally {
        if (!cancelled) setInitializing(false);
      }
    };
    void resume();
    return () => {
      cancelled = true;
    };
  }, [applyCurrentUser]);

  const loginBasic = useCallback(
    async (username: string, password: string, remember: boolean) => {
      const data = await AuthApi.login({
        username,
        password,
        timezone: new Date().getTimezoneOffset(),
      });
      applyLogin(data, remember);
    },
    [applyLogin]
  );

  const loginAzure = useCallback(async () => {
    await msalInstance.initialize();
    // Matches Triyo.Addin.Web Login.js loginAzureAd:
    // popup requests only "user.read"; claims from ID token first, then access token.
    const authResponse = await msalInstance.loginPopup({ scopes: ["user.read"] });
    const account = authResponse.account as AccountInfo | null;
    const accessToken = authResponse.accessToken ?? "";
    const idClaims = (authResponse.idTokenClaims ?? {}) as Record<string, unknown>;
    const accessClaims = decodeJwt(accessToken);
    const data = await AuthApi.azureLogin({
      username:
        account?.username ||
        readClaim(accessClaims, "preferred_username") ||
        readClaim(accessClaims, "upn") ||
        readClaim(accessClaims, "unique_name"),
      tenantId: authResponse.tenantId || readClaim(accessClaims, "tid"),
      graphConnectorId:
        readClaim(idClaims, "aud") || readClaim(accessClaims, "aud") || readClaim(accessClaims, "appid"),
      graphToken: accessToken,
      timezone: new Date().getTimezoneOffset(),
    });
    applyLogin(data);
  }, [applyLogin]);

  const loginSso = useCallback(async () => {
    const appUrl = window.APP_SETTINGS?.appUrl;
    if (!appUrl) {
      throw new Error("TRIYO web app URL (appUrl) is not configured.");
    }

    // Cancel any previous in-flight SSO wait.
    ssoAbortRef.current?.abort();
    const controller = new AbortController();
    ssoAbortRef.current = controller;

    // If a web session cookie is already present, use it without reopening the dialog.
    let cookieToken = getSsoCookieToken();
    if (!cookieToken) {
      openTriyoWebLogin(appUrl);
      cookieToken = await waitForToken(getSsoCookieToken, {
        timeoutMs: 5 * 60 * 1000,
        intervalMs: 2000,
        signal: controller.signal,
      });
    }

    // Legacy: GET /users/GetCurrentUser with the cookie token as Bearer.
    const data = await AuthApi.getCurrentUserWithToken(cookieToken);
    applyCurrentUser(data, cookieToken);
  }, [applyCurrentUser]);

  const logout = useCallback(() => {
    ssoAbortRef.current?.abort();
    removeAuthToken();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const value = useMemo(
    () => ({ user, isAuthenticated, initializing, loginBasic, loginAzure, loginSso, logout }),
    [user, isAuthenticated, initializing, loginBasic, loginAzure, loginSso, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
